import { css, Global } from '@emotion/core';
import { SessionProvider } from 'contexts/sessionContext';
import { ThemeProvider } from 'emotion-theming';
import 'i18n';
import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import { Bounce, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import App from 'screens/App';
import theme from 'static/themes/theme';
import { max } from 'utils/mediaqueryhelper';

const font = css`
    @import url('https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;500;600;700;900&display=swap')
`;

const globalStyle = css(font, {
    '*, *:before, *:after': {
        boxSizing: 'border-box',
        fontFamily: '\'Public Sans\', sans-serif'
    },
    html: {
        fontSize: theme.fontSizes.normal,
        [max[3]]: {
            fontSize: theme.fontSizes.normal - 1
        }
    },
    '::selection': {
        color: theme.colors.white,
        backgroundColor: theme.colors.primary
    },
    body: {
        position: 'relative',
        margin: 0,
        padding: 0,
        overflowX: 'hidden',
        color: theme.colors.text,
        backgroundColor: theme.colors.lightGrey,
        WebkitFontSmoothing: 'antialiased',
        WebkitOverflowScrolling: 'touch',
        MozOsxFontSmoothing: 'grayscale',
    },
    '::-webkit-scrollbar': {
        width: 7,
        WebkitAppearance: 'none'
    },
    '::-webkit-scrollbar-thumb': {
        borderRadius: 4,
        backgroundColor: 'rgba(0,0,0,.5)',
        WebkitBoxShadow: '0 0 1px rgba(255,255,255,.5)'
    },
    '#root': {
        minHeight: '100vh'
    },
    code: {
        fontFamily: `source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
        monospace`
    },
    hr: {
        marginTop: theme.constants.spacer,
        marginBottom: theme.constants.spacer,
        border: 0,
        borderBottom: '1px solid ' + theme.colors.border,
        boxSizing: 'border-box'
    },
    'h1, h2, h3, h4, h5, h6, .h0, .h1, .h2, .h3, .h4, .h5, .h6': {
        color: theme.colors.dark,
        marginTop: 0,
        marginBottom: '1rem',
        lineHeight: '1.2',
        fontWeight: 700
    },
    'h1, .h1': {
        fontSize: theme.fontSizes.h1,
        marginBottom: '1.5rem',
        textTransform: 'uppercase',
        fontWeight: 900
    },
    'h2, .h2': {
        fontSize: theme.fontSizes.h2
    },
    'h3, .h3': {
        fontSize: theme.fontSizes.h3
    },
    'h4, .h4': {
        fontSize: theme.fontSizes.h4
    },
    'h5, .h5, h6, .h6': {
        fontWeight: 600,
        fontSize: theme.fontSizes.bigger
    },
    'h5, .h5, h6, .h6, .bigger': {
        fontSize: theme.fontSizes.bigger
    },
    '.smaller': {
        fontSize: theme.fontSizes.smaller
    },
    '.dark': {
        color: theme.colors.dark
    },
    '.primary': {
        color: theme.colors.primary
    },
    p: {
        marginTop: 0,
        marginBottom: '1rem'
    },
    'li, p': {
        lineHeight: 1.5
    },
    ul: {
        marginTop: 0
    },
    'b, strong': {
        fontWeight: 600
    },
    a: {
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        '&:hover': {
            color: theme.colors.primary,
            svg: {
                '*': {
                    fill: theme.colors.primary
                }
            }
        }
    },
    img: {
        verticalAlign: 'middle'
    },
    '.react-datepicker-wrapper, .react-datepicker__input-container': {
        display: 'block !important'
    },
    table: {
        tr: {
            'td, th': {
                borderColor: theme.colors.cyan
            }
        }
    }
});


const Root = () => {
    return (
        <StrictMode>
            <Global styles={globalStyle} />
            <ThemeProvider theme={theme}>
                <SessionProvider>
                    <ToastContainer
                        transition={Bounce}
                        autoClose={2500}
                    />
                    <App />
                </SessionProvider>
            </ThemeProvider>
        </StrictMode>
    );
};

ReactDOM.render(
    <Root />,
    document.getElementById('root')
);
